import React from 'react';
import styled from 'styled-components';
import Footer from '../components/Footer';
import Header from '../components/Header';
import { WeDoDevLogo } from '../components/Logo';
import { ContactButton } from '../components';
import SEO from '../components/SEO';
import mediaQueries from '../styles/mediaQueries';

const HomeContainer = styled.div`
  max-width: 100%;
  overflow-x: hidden;
  font-family: var(--font-primary);
  
  .desktop-only {
    display: block;
    
    ${mediaQueries.medium} {
      display: none;
    }
  }
  
  .mobile-only {
    display: none;
    
    ${mediaQueries.medium} {
      display: block;
    }
  }
`;

const HeroSection = styled.section`
  position: relative;
  background-color: #011D3A;
  color: white;
  padding: 0 20px 0px;
  text-align: center;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: -20%;
    right: -20%;
    width: 40%;
    height: 70%;
    background: #8192A2;
    opacity: 0.35;
    filter: blur(80px);
    z-index: 0;
    pointer-events: none;
  }
  
  ${mediaQueries.medium} {
    padding: 0 15px 0px;
  }
  
  ${mediaQueries.small} {
    padding: 0 10px 0px;
  }
`;

const HeroTitle = styled.h1`
  position: relative;
  font-size: 48px;
  margin-bottom: 00px;
  margin-top: 20px;
  z-index: 1;
  
  ${mediaQueries.medium} {
    font-size: 36px;
  }
  
  ${mediaQueries.small} {
    font-size: 28px;
  }
`;

const HeroSubtitle = styled.h2`
  position: relative;
  font-size: 24px;
  max-width: 1200px;
  margin: 0 auto 40px;
  line-height: 1.4;
  font-weight: var(--font-weight-medium);
  z-index: 1;
  
  ${mediaQueries.medium} {
    font-size: 20px;
    margin: 0 auto 30px;
    padding: 0 15px;
  }
  
  ${mediaQueries.small} {
    font-size: 18px;
    margin: 0 auto 20px;
  }
`;

const HeroImage = styled.div`
  position: relative;
  max-width: 1200px;
  margin: 0 auto;
  z-index: 1;
  
  ${mediaQueries.large} {
    max-width: 90%;
  }
  
  ${mediaQueries.medium} {
    max-width: 95%;
  }
`;

const MissionSection = styled.section`
  position: relative;
  background-color: #011D3A;
  color: white;
  padding: 60px 20px;
  text-align: center;
  margin: 0 auto;
  
  ${mediaQueries.medium} {
    padding: 40px 20px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 15px;
  }
`;

const MissionText = styled.p`
  font-size: 18px;
  line-height: 1.6;
  position: relative;
  font-size: 24px;
  max-width: 800px;
  margin: 0 auto 40px;
  
  ${mediaQueries.medium} {
    font-size: 20px;
    padding: 0 20px;
    margin: 0 auto 30px;
  }
  
  ${mediaQueries.small} {
    font-size: 18px;
    padding: 0 15px;
    margin: 0 auto 20px;
  }
`;

const Button = styled.a`
  background-color: ${({theme}) => theme.colors.secondary};
  color: white;
  padding: 10px 20px;
  border-radius: 30px;
  text-decoration: none;
  display: inline-block;
  font-weight: var(--font-weight-medium);
  transition: background-color 0.3s;
  
  &:hover {
    background-color: #c42b12;
  }
  
  ${mediaQueries.small} {
    padding: 8px 18px;
    font-size: 14px;
  }
`;

const FeatureSection = styled.section`
  padding: 60px 60px;
  max-width: 1200px;
  margin: 0 auto;
  
  ${mediaQueries.large} {
    padding: 40px 40px;
  }
  
  ${mediaQueries.medium} {
    padding: 40px 20px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 15px;
  }
`;

const FeatureContent = styled.div`
  display: flex;
  gap: 60px;
  margin-bottom: 40px;
  
  ${mediaQueries.large} {
    gap: 40px;
  }
  
  ${mediaQueries.medium} {
    flex-direction: column;
    gap: 30px;
  }
`;

// Styles communs pour les titres principaux des sections
const SectionHeading = styled.h2`
  font-size: 48px;
  color: ${({theme}) => theme.colors.secondary};
  margin-bottom: 0;
  font-weight: bold;
  line-height: 1.1;
  
  ${mediaQueries.medium} {
    font-size: 38px;
    text-align: center;
  }
  
  ${mediaQueries.small} {
    font-size: 32px;
  }
`;

// Styles communs pour les sous-titres des sections
const SectionSubheading = styled.h3`
  font-size: 36px;
  margin-bottom: 40px;
  font-weight: bold;
  color: #011D3A;
  line-height: 1.2;
  
  ${mediaQueries.medium} {
    font-size: 30px;
    margin-bottom: 30px;
    text-align: center;
  }
  
  ${mediaQueries.small} {
    font-size: 24px;
    margin-bottom: 20px;
  }
`;

const FeatureLeft = styled.div`
  flex: 1;
  
  ${mediaQueries.medium} {
    text-align: center;
    margin-bottom: 20px;
  }

`;

const FeatureHeading = styled(SectionHeading)``;

const FeatureSubheading = styled(SectionSubheading)``;

const FeatureRight = styled.div`
  flex: 2;
  display: flex;
  flex-direction: column;
  gap: 40px;
  
  ${mediaQueries.medium} {
    padding: 0 20px;
  }
  
  ${mediaQueries.small} {
    padding: 0 20px;
    gap: 30px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  margin-top: 30px;
  
  ${mediaQueries.medium} {
    margin-top: 20px;
  }
`;

const FeatureItem = styled.div`
  display: flex;
  gap: 18px;
  align-items: flex-start;
  
  ${mediaQueries.small} {
    gap: 14px;
  }
`;

const FeatureIcon = styled.div`
  color: ${({theme}) => theme.colors.secondary};
  margin-top: 3px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    width: 24px;
    height: 24px;
    stroke-width: 1.5;
  }
`;

const FeatureItemContent = styled.div`
  h4 {
    font-size: 20px;
    margin-bottom: 8px;
    font-weight: bold;
    color: #011D3A;
  }
  
  p {
    font-size: 18px;
    color: #333;
    line-height: 1.4;
  }
  
  ${mediaQueries.medium} {
    h4 {
      font-size: 18px;
      margin-bottom: 6px;
    }
    
    p {
      font-size: 16px;
      line-height: 1.5;
    }
  }
  
  ${mediaQueries.small} {
    h4 {
      font-size: 18px;
    }
    
    p {
      font-size: 15px;
    }
  }
`;

const ProjectSection = styled.section`
  background-color: #8BC583;
  color: white;
  padding: 60px 60px;
  text-align: center;
  border-radius: 16px;
  max-width: 1100px;
  margin: 0 auto 40px;
  
  ${mediaQueries.large} {
    max-width: 90%;
    padding: 50px 40px;
  }
  
  ${mediaQueries.medium} {
    max-width: calc(100% - 40px);
    padding: 40px 30px;
    margin: 0 auto 40px;
  }
  
  ${mediaQueries.small} {
    max-width: calc(100% - 30px);
    padding: 30px 20px;
    margin: 0 auto 30px;
    border-radius: 12px;
  }
`;

const ProjectHeading = styled.h2`
  font-size: 44px;
  margin-bottom: 16px;
  font-weight: bold;
  
  &.english {
    color: #011D3A;
    margin-bottom: 10px;
  }
  
  &.french {
    font-size: 38px;
    max-width: 900px;
    margin: 0 auto 30px;
    line-height: 1.0;
  }
  
  ${mediaQueries.medium} {
    font-size: 32px;
    
    &.french {
      font-size: 28px;
      margin: 20px auto 20px;
    }
  }
  
  ${mediaQueries.small} {
    font-size: 28px;
    
    &.french {
      font-size: 28px;
      margin: 0 auto 16px;
    }
  }
`;

const UnderlinedText = styled.span`
  text-decoration: underline;
  text-underline-offset: 5px;
  text-decoration-thickness: 2px;
`;

const ProjectDescription = styled.p`
  font-size: 18px;
  max-width: 900px;
  margin: 0 auto 40px;
  line-height: 1.5;
  
  ${mediaQueries.medium} {
    font-size: 18px;
    margin: 0 auto 20px;
  }
  
  ${mediaQueries.small} {
    font-size: 18px;
    margin: 0 auto 20px;
  }
`;

const ProjectButton = styled(Button)`
  background-color: #8BC583;
  color: white;
  padding: 12px 30px;
  border: 2px solid white;
  transition: all 0.3s;
  
  &:hover {
    background-color: white;
    color: #011D3A;
  }
`;

const TeamSection = styled.section`
  display: flex;
  padding: 60px 150px;
  max-width: 1200px;
  margin: 0 auto 60px;
  gap: 60px;
  
  ${mediaQueries.large} {
    padding: 60px 80px;
    gap: 40px;
  }
  
  ${mediaQueries.medium} {
    flex-direction: column;
    padding: 40px 20px;
    gap: 30px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 15px;
    margin: 0 auto 40px;
  }
`;

const TeamLeft = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  
  ${mediaQueries.medium} {
    text-align: center;
    margin-bottom: 20px;
  }
`;

const TeamHeading = styled(SectionHeading)``;

const TeamSubheading = styled(SectionSubheading)``;

const TeamDescription = styled.p`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 40px;
  color: #333;
  max-width: 480px;
  
  ${mediaQueries.medium} {
    margin: 0 auto 30px;
    font-size: 18px;
    max-width: 90%;
  }
  
  ${mediaQueries.small} {
    font-size: 15px;
    margin: 0 auto 20px;
  }
`;

const TeamRight = styled.div`
  flex: 1;
  border-radius: 16px;
  overflow: hidden;
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    border-radius: 16px;
    max-height: 500px;
  }
  
  ${mediaQueries.medium} {
    margin: 0 auto;
    max-width: 90%;
  }
  
  ${mediaQueries.small} {
    max-width: 100%;
    font-size: 18px;
  }
`;

const TeamButton = styled(Button)`
  align-self: flex-start;
  background-color: white;
  border: 2px solid ${({theme}) => theme.colors.secondary};
  color: ${({theme}) => theme.colors.secondary};
  
  &:hover {
    background-color: ${({theme}) => theme.colors.secondary};
    color: white;
  }
  
  ${mediaQueries.medium} {
    align-self: center;
  }
`;

const TestimonialSection = styled.section`
  padding: 60px 20px;
  text-align: center;
  max-width: 900px;
  margin: 0 auto 60px;
  
  ${mediaQueries.medium} {
    padding: 40px 20px;
    margin: 0 auto 40px;
  }
  
  ${mediaQueries.small} {
    padding: 30px 15px;
    margin: 0 auto 30px;
  }
`;

const TestimonialHeading = styled.h2`
  font-size: 36px;
  margin-bottom: 40px;
  font-weight: var(--font-weight-semibold);
  span {
    color: ${({theme}) => theme.colors.secondary};
  }
  
  ${mediaQueries.medium} {
    font-size: 38px;
    margin-bottom: 30px;
    text-align: center;
  }
  
  ${mediaQueries.small} {
    font-size: 32px;
    margin-bottom: 20px;
  }
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 60px;
  
  ${mediaQueries.medium} {
    grid-template-columns: 1fr;
    gap: 30px;
  }
  
  ${mediaQueries.small} {
  }
`;

const TestimonialCard = styled.div`
  text-align: left;
  
  p {
    font-size: 16px;
    line-height: 1.6;
    margin-bottom: 16px;
    font-style: normal;
  }
  
  ${mediaQueries.medium} {
    padding: 0 10px;
  }
  
  ${mediaQueries.small} {
    margin-bottom: 20px;
    padding: 0;
  }
`;

const TestimonialAuthor = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  
  img {
    width: 40px;
    height: 40px;
    border-radius: 50%;
    object-fit: cover;
  }
  
  div {
    h4 {
      font-size: 16px;
      margin-bottom: 4px;
      font-weight: var(--font-weight-semibold);
    }
    
    p {
      font-size: 14px;
      margin: 0;
      color: #666;
      font-style: normal;
    }
  }
  
  ${mediaQueries.small} {
    img {
      width: 36px;
      height: 36px;
    }
    
    div {
      h4 {
        font-size: 15px;
      }
      
      p {
        font-size: 11px;
      }
    }
  }
`;

const Home: React.FC = () => {
  return (
    <HomeContainer data-component="HomeContainer">
      <SEO
        title="Développement web et mobile sur mesure" 
        description="Thinkfab transforme vos sujets complexes en solutions logicielles simples. Développement web et mobile sur mesure pour digitaliser votre entreprise."
        canonical={`${window.location.origin}/`}
        image="/images/home/wdd-home.png"
      />
      <HeroSection data-component="HeroSection">
        <Header backgroundColor="transparent" /> {/* Transparent header for gradient background */}
        
        <HeroTitle data-component="HeroTitle">
          <WeDoDevLogo 
            weDoColor="white" 
            devColor="#DD3114" 
            size="40px" 
          />
        </HeroTitle>
        <HeroSubtitle data-component="HeroSubtitle">
          Nous transformons vos sujets complexes en solutions simples.
        </HeroSubtitle>
        
        <HeroImage data-component="HeroImage">
          <img 
            src="/images/home/wdd-home.png" 
            alt="Équipe de développement en action" 
            style={{ 
              width: '100%', 
              maxHeight: '400px', 
              objectFit: 'cover', 
              objectPosition: 'center 25%', 
              borderRadius: '16px' 
            }} 
          />
        </HeroImage>
      </HeroSection>
      
      <MissionSection data-component="MissionSection">
        <MissionText data-component="MissionText">
          Thinkfab a pour mission de conseiller, co-construire et développer des logiciels sur mesure au service de la digitalisation des entreprises et des institutions.
        </MissionText>
        <Button href="/metier" data-component="Button">En savoir plus</Button>
      </MissionSection>
      
      <FeatureSection data-component="FeatureSection">
        <FeatureContent>
          <FeatureLeft data-component="FeatureLeft">
            <FeatureHeading className="desktop-only" data-component="FeatureHeading">We do<br/>software.</FeatureHeading>
            <FeatureHeading className="mobile-only" data-component="FeatureHeading">We do software.</FeatureHeading>
            <FeatureSubheading className="desktop-only" data-component="FeatureSubheading">Le goût du<br/>logiciel bien<br/>fait.</FeatureSubheading>
            <FeatureSubheading className="mobile-only" data-component="FeatureSubheading">Le goût du logiciel bien fait.</FeatureSubheading>
          </FeatureLeft>
          
          <FeatureRight data-component="FeatureRight">
            <FeatureItem data-component="FeatureItem">
              <FeatureIcon data-component="FeatureIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l3 3 5-5" />
                </svg>
              </FeatureIcon>
              <FeatureItemContent>
                <h4>Simplexité</h4>
                <p>Nous aimons les sujets complexes et proposer des solutions simples.</p>
              </FeatureItemContent>
            </FeatureItem>
            
            <FeatureItem data-component="FeatureItem">
              <FeatureIcon data-component="FeatureIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l3 3 5-5" />
                </svg>
              </FeatureIcon>
              <FeatureItemContent>
                <h4>Une seule équipe</h4>
                <p>Pour nous, il n'y a qu'une seule équipe : celle du projet. Nous aimons intégrer l'équipe de notre client dans l'organisation quotidienne du projet.</p>
              </FeatureItemContent>
            </FeatureItem>
            
            <FeatureItem data-component="FeatureItem">
              <FeatureIcon data-component="FeatureIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l3 3 5-5" />
                </svg>
              </FeatureIcon>
              <FeatureItemContent>
                <h4>Méthode agile</h4>
                <p>Scrum, Kanban, c'est évident, mais c'est mieux de le préciser.</p>
              </FeatureItemContent>
            </FeatureItem>
            
            <FeatureItem data-component="FeatureItem">
              <FeatureIcon data-component="FeatureIcon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M8 12l3 3 5-5" />
                </svg>
              </FeatureIcon>
              <FeatureItemContent>
                <h4>Technologie</h4>
                <p>Nous aimons la technologie, toujours au service du métier et de ses besoins.</p>
              </FeatureItemContent>
            </FeatureItem>
          </FeatureRight>
        </FeatureContent>
        
        <ButtonContainer>
          <Button href="/agence">En savoir plus</Button>
        </ButtonContainer>
      </FeatureSection>

      <ProjectSection data-component="ProjectSection">
        <ProjectHeading className="english" data-component="ProjectHeading">We love challenges.</ProjectHeading>
        <ProjectHeading className="french" data-component="ProjectHeading">Nous réalisons des projets ambitieux, notamment <UnderlinedText>dans le domaine de la santé</UnderlinedText>.</ProjectHeading>
        <ProjectDescription data-component="ProjectDescription">
        Une passion pour l'innovation, une expertise en développement logiciel : Explorez nos succès dans la création de solutions technologiques adaptées à différents secteurs, dont le médical où notre savoir-faire fait la différence.
        </ProjectDescription>
        <ProjectButton href="/projets" data-component="ProjectButton">En savoir plus</ProjectButton>
      </ProjectSection>
      
      <TeamSection data-component="TeamSection">
        <TeamLeft data-component="TeamLeft">
          <TeamHeading data-component="TeamHeading">We are people.</TeamHeading>
          <TeamSubheading className="desktop-only" data-component="TeamSubheading">Humains<br/>et ensemble<br/>avant tout.</TeamSubheading>
          <TeamSubheading className="mobile-only" data-component="TeamSubheading">Humains et ensemble avant tout.</TeamSubheading>
          <TeamDescription data-component="TeamDescription">
            Nous sommes convaincus que la bienveillance et le respect de chacun au sein du groupe sont les bons moyens pour apprendre, partager et évoluer.
          </TeamDescription>
          <TeamButton href="/agence" data-component="TeamButton">En savoir plus</TeamButton>
        </TeamLeft>
        
        <TeamRight data-component="TeamRight">
          <img 
            src="/images/home/wdd-wearepeople.png" 
            alt="Notre équipe en collaboration" 
          />
        </TeamRight>
      </TeamSection>
      
      <TestimonialSection data-component="TestimonialSection">
        <TestimonialHeading data-component="TestimonialHeading">
          Ils aiment travailler <span>avec nous.</span>
        </TestimonialHeading>
        
        <TestimonialGrid data-component="TestimonialGrid">
          <TestimonialCard data-component="TestimonialCard">
            <p>"Logitio propose des solutions innovantes pour optimiser la gestion des ressources humaines.
L'univers RH est exigeant et connait actuellement de très fortes évolutions. Travailler avec la société ThinkFab nous permet de concevoir des solutions d'une excellente ergonomie, reposant sur les technologies web les plus récentes. Fabrice et Kévin font preuve de réactivité et ne cesse de nous surprendre en étant toujours force de propositions créatives et audacieuses.
Nous ne saurions mieux faire que de vous recommander ThinkFab pour vos futurs projets !"</p>
            <TestimonialAuthor data-component="TestimonialAuthor">
              <div>
                <h4>Joel Albecker</h4>
                <p>PDG • Logitio</p>
              </div>
            </TestimonialAuthor>
          </TestimonialCard>
          
          <TestimonialCard data-component="TestimonialCard">
            <p>"Nous sommes une entreprise de géomarketing unique en son genre centrée sur l’innovation. Nous produisons et gérons des bases de données recensant l’ensemble de la distribution de la mode en France et inventons les services en ligne permettant à nos clients d’exploiter ces informations. Depuis la création du projet initial, il y a de cela 20 ans, Fabrice PAPST nous accompagne dans cette aventure jalonnée d’innovations technologiques.
            Le succès de nos projets dépend pour beaucoup de cet accompagnement que je résumerai en 3 termes concernant notre partenaire : l’écoute, l’analyse et les propositions."</p>
            <TestimonialAuthor data-component="TestimonialAuthor">
              <div>
                <h4>Philippe Zeder</h4>
                <p>PDG • Groupe Labomode</p>
              </div>
            </TestimonialAuthor>
          </TestimonialCard>
          

        </TestimonialGrid>
      </TestimonialSection>
      
      <Footer 
        pageContext="Page d'accueil"
        heading={
          <>
            Rencontrons-nous
            <span role="img" aria-label="waving hand">👋</span>
          </>
        }
        subheading="Discutons de votre projet."
        customButton={<ContactButton variant="secondary">Contactez-nous</ContactButton>}
      />
    </HomeContainer>
  );
};

export default Home;